import { useEffect, useState } from "react";
import {
  adjustableDifficultyRoomIds,
  defaultRoomDifficulty,
  resolveRoomDifficulty,
  roomLabels,
} from "../../Config/config";
import { useDifficultyRatings } from "../../Context/DifficultyRatingsContext";
import { DifficultyLevel, DifficultyRatings } from "../../Types/Game";
import DifficultyIndicator, {
  DIFFICULTY_NAMES,
} from "../../Components/DifficultyIndicator/DifficultyIndicator";
import "./DifficultyRatingsModal.css";

/**
 * DifficultyRatingsModal
 * ----------------------
 * Lets an admin raise/lower each room's difficulty rating for every event.
 * Edits a draft copy and only saves (straight to the API) on Save —
 * Cancel/Escape discards. A rating equal to the room's default is saved as
 * "no override", so unedited rooms keep tracking the built-in defaults.
 */

// TYPES -------------------------------------------------------------
type Props = {
  onClose: () => void;
};

type Ratings = Record<string, DifficultyLevel>;

// COMPONENT ---------------------------------------------------------
const DifficultyRatingsModal = ({ onClose }: Props) => {
  const { ratings: savedRatings, saveRatings } = useDifficultyRatings();

  const [ratings, setRatings] = useState<Ratings>(() =>
    Object.fromEntries(
      adjustableDifficultyRoomIds.map((id) => [id, resolveRoomDifficulty(id, savedRatings)])
    )
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Escape closes without saving, like Cancel
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isSaving) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, isSaving]);

  // HANDLERS --------------------------------------------------------
  const adjust = (id: string, delta: 1 | -1) => {
    setRatings((prev) => {
      const next = Math.min(4, Math.max(1, prev[id] + delta)) as DifficultyLevel;
      return { ...prev, [id]: next };
    });
  };

  const resetToDefaults = () => {
    setRatings(
      Object.fromEntries(adjustableDifficultyRoomIds.map((id) => [id, defaultRoomDifficulty[id]]))
    );
  };

  const save = async () => {
    const overrides: DifficultyRatings = {};
    adjustableDifficultyRoomIds.forEach((id) => {
      if (ratings[id] !== defaultRoomDifficulty[id]) overrides[id] = ratings[id];
    });

    setIsSaving(true);
    setError(null);
    try {
      await saveRatings(overrides);
      onClose();
    } catch {
      setError("Couldn't save the difficulty ratings. Please try again.");
      setIsSaving(false);
    }
  };

  const hasOverrides = adjustableDifficultyRoomIds.some(
    (id) => ratings[id] !== defaultRoomDifficulty[id]
  );

  // RENDER ----------------------------------------------------------
  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="difficultyRatingsTitle"
      onClick={() => !isSaving && onClose()}
    >
      <div className="modal-card difficulty-ratings" onClick={(e) => e.stopPropagation()}>
        <h3 id="difficultyRatingsTitle">Adjust difficulty ratings</h3>
        <p>Set how hard each Escape Room is rated. Changes apply to every event.</p>

        <ul className="difficulty-ratings__list">
          {adjustableDifficultyRoomIds.map((id) => {
            const level = ratings[id];
            const name = roomLabels[id] ?? id;
            const isChanged = level !== defaultRoomDifficulty[id];
            return (
              <li key={id} className="difficulty-ratings__row">
                <span className="difficulty-ratings__room">
                  {name}
                  {isChanged && (
                    <span
                      className="difficulty-ratings__changed"
                      title={`Default: ${DIFFICULTY_NAMES[defaultRoomDifficulty[id]]}`}
                    >
                      edited
                    </span>
                  )}
                </span>
                <span className="difficulty-ratings__level">
                  <DifficultyIndicator level={level} className="difficulty-ratings__icon" />
                  <span className="difficulty-ratings__level-name">{DIFFICULTY_NAMES[level]}</span>
                </span>
                <span className="difficulty-ratings__controls">
                  <button
                    type="button"
                    className="btn-blue btn-icon-only difficulty-ratings__step"
                    onClick={() => adjust(id, -1)}
                    disabled={isSaving || level <= 1}
                    aria-label={`Decrease ${name} difficulty`}
                  >
                    &minus;
                  </button>
                  <button
                    type="button"
                    className="btn-blue btn-icon-only difficulty-ratings__step"
                    onClick={() => adjust(id, 1)}
                    disabled={isSaving || level >= 4}
                    aria-label={`Increase ${name} difficulty`}
                  >
                    +
                  </button>
                </span>
              </li>
            );
          })}
        </ul>

        {error && <p className="difficulty-ratings__error">{error}</p>}

        <div className="difficulty-ratings__footer">
          <button
            type="button"
            className="btn-grey"
            onClick={resetToDefaults}
            disabled={isSaving || !hasOverrides}
          >
            Reset to defaults
          </button>
          <div className="button-rail">
            <button type="button" className="btn-blue" onClick={save} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save"}
            </button>
            <button type="button" className="btn-grey" onClick={onClose} disabled={isSaving}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DifficultyRatingsModal;
